import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminAlerts, acknowledgeAdminAlert, getSeverityBadge } from '../../services/complianceService';
import toast from 'react-hot-toast';

export default function AdminAlertsTab() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAcknowledged, setShowAcknowledged] = useState(false);

  const load = () => {
    setLoading(true);
    getAdminAlerts({ acknowledged: showAcknowledged ? undefined : false })
      .then((res) => setAlerts(res.alerts || []))
      .catch((err) => toast.error(err.message || 'Could not load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [showAcknowledged]);

  const handleAck = async (alertId) => {
    try { await acknowledgeAdminAlert(alertId); load(); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/admin')}
          className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">
          ← Admin dashboard
        </button>
      </div>

      <h1 className="font-serif text-3xl text-brand-dark mb-2">Admin alerts</h1>
      <p className="text-body text-[0.9rem] mb-4">
        Critical and high-severity events that need immediate attention.
      </p>

      <label className="flex items-center gap-2 mb-4 cursor-pointer">
        <input type="checkbox" checked={showAcknowledged}
          onChange={(e) => setShowAcknowledged(e.target.checked)} />
        <span className="text-[0.85rem]">Show acknowledged alerts too</span>
      </label>

      {loading && <div className="p-10 text-center text-muted">Loading...</div>}

      {!loading && alerts.length === 0 && (
        <div className="p-10 text-center text-muted bg-white rounded-2xl border border-slate-200">
          {showAcknowledged ? 'No alerts' : 'No unacknowledged alerts — all clear!'}
        </div>
      )}

      <div className="grid gap-2.5">
        {alerts.map((a) => {
          const sev = getSeverityBadge(a.severity);
          const created = a.createdAt?._seconds ? new Date(a.createdAt._seconds * 1000) : null;
          return (
            <div key={a.id}
              className={`p-3.5 rounded-xl border ${a.acknowledged ? 'bg-slate-50 border-slate-200 opacity-70' : 'bg-white border-amber-200'}`}>
              <div className="flex justify-between mb-1.5">
                <div className="font-bold text-brand-dark">{a.subject}</div>
                <span className="px-2 py-0.5 rounded-full text-[0.7rem] font-bold"
                  style={{ background: sev.bg, color: sev.color }}>
                  {sev.label}
                </span>
              </div>
              <div className="text-[0.85rem] text-body whitespace-pre-wrap mb-2">
                {a.body}
              </div>
              <div className="flex justify-between items-center text-[0.78rem]">
                <span className="text-muted">
                  {created ? created.toLocaleString('en-AU') : ''}
                  {a.acknowledged && ` · ✓ Acknowledged`}
                </span>
                <div className="flex gap-1.5">
                  {a.link && (
                    <button onClick={() => navigate(a.link)}
                      className="px-2.5 py-1 rounded-md border border-brand-primary bg-white text-brand-primary cursor-pointer text-[0.78rem] font-semibold">
                      View
                    </button>
                  )}
                  {!a.acknowledged && (
                    <button onClick={() => handleAck(a.id)}
                      className="px-2.5 py-1 rounded-md border-0 bg-brand-primary text-white cursor-pointer text-[0.78rem] font-semibold">
                      Acknowledge
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}